import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import { startWorkflowRun, WorkflowRunStartResult } from "../services/workflowService";

export type WorkflowRunStartState =
  | { status: "idle" }
  | { status: "starting" }
  | { status: "started"; instanceId: number }
  | { status: "not-found" }
  | { status: "not-runnable" }
  | { status: "forbidden" }
  | { status: "error" };

export interface WorkflowRunStartRequest {
  definitionId: number;
  /** One key per Run / Test click. The Run screen keeps it in the URL, so a refresh before the
   *  redirect replays the same case on the server instead of creating a second one. */
  idempotencyKey: string;
  /** Epic 80431 - a Builder test run; kept out of Instances, My Work and the nav badge. */
  isTest?: boolean;
  /** Epic 80431 - with isTest: discard the caller's open test runs of the workflow first. */
  discardOpenTestRuns?: boolean;
}

/**
 * Starts a case for the Run screen. Pass `null` when there is nothing to start and the hook stays
 * idle. Unexpected failures go through useApi's central alert and land in `error`; `retry` sends
 * the same idempotency key again, because it is the same click being retried.
 */
const useWorkflowRunStart = (request: WorkflowRunStartRequest | null) => {
  const { executeApi } = useApi();
  const definitionId = request?.definitionId ?? null;
  const idempotencyKey = request?.idempotencyKey ?? null;
  const isTest = request?.isTest ?? false;
  const discardOpenTestRuns = request?.discardOpenTestRuns ?? false;
  const [state, setState] = useState<WorkflowRunStartState>({
    status: definitionId && idempotencyKey ? "starting" : "idle",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!definitionId || !idempotencyKey) {
      setState({ status: "idle" });
      return undefined;
    }
    let active = true;
    setState({ status: "starting" });
    executeApi<WorkflowRunStartRequest, WorkflowRunStartResult>(startWorkflowRun, {
      definitionId,
      idempotencyKey,
      // Only sent when true, so an ordinary start's request body is unchanged.
      ...(isTest ? { isTest } : {}),
      ...(isTest && discardOpenTestRuns ? { discardOpenTestRuns } : {}),
    }).then((result) => {
      if (!active) return;
      if (!result) setState({ status: "error" });
      else if (result.kind === "started")
        setState({ status: "started", instanceId: result.result.instanceId });
      else setState({ status: result.kind });
    });
    return () => {
      active = false;
    };
  }, [executeApi, definitionId, idempotencyKey, isTest, discardOpenTestRuns, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { state, retry };
};

export default useWorkflowRunStart;
